import React, { useContext, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { getModerators, getProjects } from '../service';
import { AdminContext } from '../contexts/admin.context';

import Button from '../components/common/Button';
import Loader from '../components/common/Loader';
import { ClassTable } from '../components';
import ProjectCard from '../components/project/ProjectCard';
import { trackEvent } from '../utils';
import { InfoCircleIcon } from '../components/icons';
import { hexToRgba } from '../utils/colors';
import { getTranslation } from '../utils/translation';
import ClipboardCopyField from '../components/common/ClipboardCopyField';

const VIP_TEST_TYPE = {
  vipTestAndProfileSetup: 'vipTestAndProfileSetup',
  vipTestOnly: 'vipTestOnly'
};

const ManageExistingProject = ({ type = 'current' }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [classes, setClasses] = useState([]);
  const [progress, setProgress] = useState(0);
  const [isLoading, setLoading] = useState(true);
  const [moderators, setModerators] = useState([]);
  const {
    themeId,
    teamType,
    setContentPopupInfo,
    user,
    portalConfig: {
      classServices,
      enableModerators,
      parentsVIPProductId,
      projectTypes,
      teachersPortalFeatures: { portalTheme = {}, enableHistoryTab, historyTabDate } = {}
    } = {},
    themeConfig: { primaryVariant, secondaryVariant, intensitySecondary }
  } = useContext(AdminContext);

  const basePath = `/${themeId}/${teamType}`;

  const buildTableHeaders = () => {
    const headers = [];

    // Fixed headers
    headers.push(<ClassTableHeader t={t} id="name" firstHeader />);
    headers.push(<ClassTableHeader t={t} id="projectType" />);
    if (enableModerators && user?.administrator)
      headers.push(<ClassTableHeader t={t} id="moderator" />);

    const sortedKeys = Object.keys(classServices).sort((a, b) => {
      return classServices[a].order - classServices[b].order;
    });

    // Master admin features
    sortedKeys.forEach((key) => {
      if (key === 'transferStudent') return; // Displayed outside table
      const { enabled, enabledForHistoryTab } = classServices[key];

      if ((type === 'current' && enabled) || (type === 'history' && enabledForHistoryTab)) {
        if (key === 'parentVipTestLink' && parentsVIPProductId) {
          headers.push(<ClassTableHeader key={key} t={t} id={'parentVIPTestLink'} />);
          return;
        }
        headers.push(<ClassTableHeader t={t} id={key} />);
      }
    });

    return headers;
  };

  const buildTaleRows = () => {
    const rows = [];

    classes.forEach((classItem) => {
      const row = [];

      row.push(
        <p
          className="text-base"
          style={{
            color: portalTheme?.projectsTableFirstColumnTextColor
          }}>
          {classItem?.specs?.description}
        </p>
      );

      // Project Type
      row.push(
        <ProjectCard
          project={classItem}
          serviceId={'projectType'}
          historyTab={type === 'history'}
          classes={classes}
          setClasses={setClasses}
        />
      );

      // Moderator
      if (enableModerators && user?.administrator)
        row.push(
          <ProjectCard
            project={classItem}
            serviceId={'moderator'}
            historyTab={type === 'history'}
            classes={classes}
            setClasses={setClasses}
            moderators={moderators}
          />
        );

      const sortedKeys = Object.keys(classServices).sort((a, b) => {
        return classServices[a].order - classServices[b].order;
      });

      sortedKeys.forEach((key) => {
        if (key === 'transferStudent') return; // Displayed outside table
        const { enabled, enabledForHistoryTab } = classServices[key];
        const projectTypeObject = projectTypes?.find((p) => p.id === classItem.specs.projectType);

        if ((type === 'current' && enabled) || (type === 'history' && enabledForHistoryTab)) {
          if (key === 'vipTestLink') {
            if (projectTypeObject?.profileSetupType === VIP_TEST_TYPE.vipTestAndProfileSetup) {
              classItem.specs.vipTestUrl = `${
                process.env.REACT_APP_MF_SITE_DOMAIN || 'https://www.mission-future.com'
              }/${themeId}/${teamType}/${classItem?.accountId}/${classItem?.id}/vip`;
            } else {
              classItem.specs.vipTestUrl = classItem.specs.url;
            }
          }

          row.push(
            <ProjectCard
              project={classItem}
              serviceId={key}
              classes={classes}
              setClasses={setClasses}
              resources={projectTypeObject?.resources}
            />
          );
        }
      });

      rows.push(row);
    });

    return rows;
  };

  const fetchClasses = async () => {
    try {
      setLoading(true);
      const response = await getProjects();
      if (response?.status === 200) {
        setClasses(response.data);
        if (type === 'history' && enableHistoryTab && new Date(historyTabDate)?.getTime()) {
          const filteredClasses = response.data.filter((classItem) => {
            return new Date(classItem.created) < new Date(historyTabDate);
          });
          setClasses(filteredClasses || []);
        }

        if (type === 'current' && enableHistoryTab && new Date(historyTabDate)?.getTime()) {
          const filteredClasses = response.data.filter((classItem) => {
            return new Date(classItem.created) >= new Date(historyTabDate);
          });
          setClasses(filteredClasses || []);
        }

        setProgress(100);
        return;
      }
      toast.error(t('errors.failed-load-courses'));
    } catch (error) {
      setProgress(100);
      console.error('ERROR', error);
    }
  };

  const fetchModerators = async () => {
    try {
      const response = await getModerators();
      setModerators(response.data);
    } catch (error) {
      console.error('ERROR', error);
    }
  };

  // Get classes
  useEffect(() => {
    fetchClasses();
    fetchModerators();
  }, []);

  useEffect(() => {
    trackEvent('page_view', {
      organization: themeId === 'ch' ? 'SSK' : 'WSF'
    });
  }, []);

  if (type === 'history' && (!enableHistoryTab || !new Date(historyTabDate)?.getTime())) {
    return <Navigate to={`${basePath}/projects`} replace />;
  }

  return (
    <>
      <div className="flex flex-col items-start">
        {type === 'current' && (
          <div className="mb-5 w-full">
            <h1
              className="text-2xl font-bold text-left text-cblack-primary mb-2.5"
              dangerouslySetInnerHTML={{ __html: getTranslation(t, 'manageProjects.header') }}
            />
            <div
              className="mb-2.5"
              dangerouslySetInnerHTML={{ __html: getTranslation(t, 'manageProjects.subHeader') }}
            />
          </div>
        )}
        {type === 'history' && (
          <div className="mb-5 w-full">
            <h1
              className="text-2xl font-bold text-left text-cblack-primary mb-2.5"
              dangerouslySetInnerHTML={{
                __html: getTranslation(t, 'manageProjectsHistory.header')
              }}
            />
            <div
              className="mb-2.5"
              dangerouslySetInnerHTML={{
                __html: getTranslation(t, 'manageProjectsHistory.subHeader')
              }}
            />
          </div>
        )}
        {isLoading ? (
          <Loader progress={progress} setLoading={setLoading} setProgress={setProgress} />
        ) : (
          <>
            <div className={`w-full ${type === 'history' ? 'grayscale' : ''}`}>
              <ClassTable
                headers={buildTableHeaders()}
                rows={buildTaleRows()}
                thumbColor={hexToRgba(primaryVariant, '100%')}
                thumbBackground={hexToRgba(primaryVariant, '10%')}
                headerBgDark={hexToRgba(secondaryVariant, '100%')}
                headerBg={hexToRgba(secondaryVariant, intensitySecondary)}
                pagination={5}
                scrollWrapper={true}
              />
            </div>

            {type === 'current' && (
              <div className="mt-4">
                <Button
                  buttonType={'primary'}
                  onClick={() => navigate(basePath + '/create-project')}>
                  {getTranslation(t, 'manageProjects.createNewButton')}
                </Button>
              </div>
            )}

            {type === 'current' && classServices?.transferStudent?.enabled && (
              <div className="mt-4">
                <div
                  className="my-1 text-lg"
                  dangerouslySetInnerHTML={{
                    __html: getTranslation(t, 'manageProjects.transferStudent')
                  }}
                />

                <ClipboardCopyField
                  textToCopy={`${window.location.origin}${basePath}/transfer-student/${user?.accounts?.[0]?.id}`}
                  isClickable={true}
                />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

const ClassTableHeader = ({ t, id, firstHeader = false }) => {
  const {
    setContentPopupInfo,
    portalConfig: { teachersPortalFeatures: { portalTheme = {} } = {} } = {}
  } = useContext(AdminContext);

  const headerText = {
    tableHeading: getTranslation(t, `class.table.${id}.tableHeading`),
    heading: getTranslation(t, `class.table.${id}.popupHeading`),
    content: getTranslation(t, `class.table.${id}.popupContent`)
  };

  if (headerText?.heading?.includes(`class.table.${id}.popupHeading`)) headerText.heading = null;
  if (headerText?.content?.includes(`class.table.${id}.popupContent`)) headerText.content = null;

  const showInfo = () => {
    setContentPopupInfo({
      isVisible: true,
      content: (
        <div className="w-full text-left p-6">
          <h1 className="text-xl font-semibold">{headerText?.heading}</h1>
          <p>{headerText?.content}</p>
        </div>
      )
    });
  };

  return (
    <div
      className="flex gap-4 items-center font-semibold text-black"
      style={{
        color: firstHeader
          ? portalTheme?.projectsTableFirstColumnTextColor
          : portalTheme?.projectsTableHeadersTextColor
      }}>
      <span
        className="text-left"
        dangerouslySetInnerHTML={{ __html: headerText?.tableHeading ?? id }}
      />
      {headerText?.heading && headerText?.content && (
        <button onClick={showInfo}>
          <InfoCircleIcon />
        </button>
      )}
    </div>
  );
};

export default ManageExistingProject;
